import { useLayoutEffect, useState } from 'react';

/** Visible label before CMP injects copy into the hidden host, or when no host id is found. */
export const PRIVACY_EMBED_ROW_FALLBACK = 'Tracking Preferences';

export function parseHostIdFromHtmlFragment(html: string | undefined): string | undefined {
  if (!html) return undefined;
  const attrId = /id\s*=\s*(["'])([^"'>\s]+)\1/i;
  return attrId.exec(html)?.[2];
}

export function activatePrivacyHtmlEmbed(htmlFragment?: string) {
  if (typeof document === 'undefined' || !htmlFragment) return;
  const hostId = parseHostIdFromHtmlFragment(htmlFragment);
  if (hostId) {
    const root = document.getElementById(hostId);
    const clickable = root?.querySelector<HTMLElement>('a[href],button');
    if (clickable) {
      clickable.click();
      return;
    }
  }
  const w = window as Window & { Optanon?: { ToggleInfoDisplay?: () => void } };
  w.Optanon?.ToggleInfoDisplay?.();
}

/** Reads visible text CMP injects into the hidden host when the settings overlay opens. */
export function usePrivacyEmbedMirrorText(htmlContent: string | undefined, cmsLabel: string): string {
  const cms = cmsLabel.trim();
  const [text, setText] = useState(() => cms || PRIVACY_EMBED_ROW_FALLBACK);

  useLayoutEffect(() => {
    const hostId = parseHostIdFromHtmlFragment(htmlContent);
    const initial = cms || PRIVACY_EMBED_ROW_FALLBACK;
    setText(initial);

    if (!hostId || typeof document === 'undefined') return;

    const readHost = () => {
      const el = document.getElementById(hostId);
      if (!el) return;
      const raw = el.innerText?.replace(/\s+/g, ' ').trim() ?? '';
      if (raw.length > 0) setText(raw);
    };

    readHost();
    const raf = requestAnimationFrame(() => readHost());

    return () => cancelAnimationFrame(raf);
  }, [htmlContent, cms]);

  return text;
}
